import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Run } from "../lib/supabase";
import RunForm from "../components/runs/RunForm";
import LoadingScreen from "../components/ui/LoadingScreen";
import toast from "react-hot-toast";
import { ENTITY_NAMES } from "../constants/entities";
import SmartBackButton from "../components/ui/SmartBackButton";
import { normalizeDaysOfWeek } from "../utils/runs";

interface RunWithSeries extends Run {
  series: {
    id: string;
    title: string;
    code: string;
  };
}

const EditRun: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [run, setRun] = useState<RunWithSeries | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRun = async () => {
      try {
        if (!id) return;
        const { data, error } = await supabase
          .from("runs")
          .select(
            `
          *,
          series:series_id (
            id,
            title,
            code
          )
        `
          )
          .eq("id", id)
          .single();

        if (error) throw error;
        setRun(data);
      } catch (error) {
        console.error("Error fetching run:", error);
        toast.error("Failed to fetch run details");
      } finally {
        setIsFetching(false);
      }
    };

    fetchRun();
  }, [id]);

  const handleUpdateRun = async (data: Partial<Run>) => {
    try {
      setIsLoading(true);
      if (!id) return;

      const { error } = await supabase
        .from("runs")
        .update({
          series_id: data.series_id,
          name: data.name || "",
          day_of_week: normalizeDaysOfWeek(data.day_of_week || []),
          start_time: data.start_time || "",
          end_time: data.end_time || "",
          start_date: data.start_date || "",
          location: data.location || "",
          instructor: data.instructor || "",
          max_students: data.max_students || 20,
          color: data.color || "#3B82F6",
          price: data.price || 0,
          active: data.active ?? true,
        })
        .eq("id", id);

      if (error) throw error;

      toast.success(`${ENTITY_NAMES.RUN} updated successfully`);
      navigate(`/runs/${id}`);
    } catch (error) {
      console.error("Error updating run:", error);
      toast.error(`Failed to update ${ENTITY_NAMES.RUN.toLowerCase()}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingScreen />;

  if (!run)
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-bold mb-2">{ENTITY_NAMES.RUN} Not Found</h2>
        <SmartBackButton fallbackPath="/series" className="btn btn-primary" />
      </div>
    );

  return (
    <div>
      <div className="flex items-center mb-6">
        <SmartBackButton
          fallbackPath={`/runs/${id}`}
          className="text-link hover:text-hover flex items-center"
        >
          Back to {ENTITY_NAMES.RUN} Details
        </SmartBackButton>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h1 className="">
              Edit {ENTITY_NAMES.RUN} {run.name} of {run.series.code}
            </h1>
          </div>

          <RunForm
            defaultValues={run}
            onSubmit={handleUpdateRun}
            isLoading={isLoading}
            seriesId={run.series_id}
            onCancel={() => navigate(`/runs/${id}`)}
          />
        </div>
      </div>
    </div>
  );
};

export default EditRun;
